import React, { useMemo } from 'react';
import { injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { useTheme } from '@material-ui/core/styles';

import ChartCard from '~/components/ChartCard';
import LocaleMessage from '~/components/LocaleMessage';

import GetPluginspaceTheme from '~/util/PluginspaceTheme';

import { ChatCardContainer } from '../../Dashboard/styles';
import {
    buildProductConsumptionSeries,
    formatFluidVolume,
} from './cleaningReportConsumptionUtils';
import { getCleaningReportChartColors } from './cleaningReportUtils';

function ProductTooltip({ active, payload, unitLabel }) {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload || {};
    return (
        <div className="jr-card p-2 mb-0">
            <div>
                <strong>{row.date}</strong>
            </div>
            <div>
                {row.displayValue != null
                    ? `${row.displayValue} ${unitLabel}`
                    : '—'}
            </div>
        </div>
    );
}

ProductTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(PropTypes.shape({})),
    unitLabel: PropTypes.string,
};

ProductTooltip.defaultProps = {
    active: false,
    payload: [],
    unitLabel: 'ml',
};

function CleaningReportProductSpentCard({
    list,
    consumption,
    robotDilutions,
    periodDates,
}) {
    const theme = useTheme();
    const colors = GetPluginspaceTheme(theme);
    const { graphColor } = getCleaningReportChartColors(colors);
    const productColor = colors.secondary || '#7c3aed';

    const settings = useSelector(state => state.settings || null);
    const darkMode = !!(settings && settings.dark_mode);
    const tickFill = darkMode ? '#bdbdbd' : '#616161';
    const gridStroke = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

    const seriesResult = useMemo(
        () =>
            buildProductConsumptionSeries({
                list,
                consumption,
                robotDilutions,
                periodDates,
            }),
        [list, consumption, robotDilutions, periodDates]
    );

    const totalMl = seriesResult.totalMl || 0;
    const productFmt = formatFluidVolume(totalMl);
    const useLiters = totalMl >= 1000;
    const unitLabel = useLiters ? 'L' : 'ml';

    const chartData = useMemo(() => {
        return (seriesResult.data || []).map(point => {
            const rawMl = point.value || 0;
            const num = useLiters
                ? Number((rawMl / 1000).toFixed(2))
                : Number(rawMl.toFixed(1));
            return {
                ...point,
                displayValue: num,
            };
        });
    }, [seriesResult.data, useLiters]);

    const headerValue =
        totalMl > 0 ? `${productFmt.value} ${productFmt.unit}` : '—';

    return (
        <div className="col-md-6 col-12 mb-5 dash-card">
            <ChatCardContainer>
                <ChartCard
                    className="animated slideInFromLeft animation-duration-3"
                    title={
                        <LocaleMessage msg="page.robots.cleaning_report.product_spent.title" />
                    }
                    value={headerValue}
                    desc={
                        <LocaleMessage msg="page.robots.cleaning_report.product_spent.desc" />
                    }
                >
                    <div className="row">
                        <div className="col-12 mb-3 area-chart-container">
                            <ResponsiveContainer
                                className="card-img-bottom overflow-hidden animated slideInFromLeft animation-duration-5"
                                width="100%"
                                height={260}
                            >
                                <AreaChart
                                    data={chartData}
                                    margin={{
                                        top: 16,
                                        right: 16,
                                        left: 8,
                                        bottom: 8,
                                    }}
                                >
                                    <defs>
                                        <linearGradient
                                            id="product-spent-gradient"
                                            x1="0"
                                            y1="0"
                                            x2="0"
                                            y2="1"
                                        >
                                            <stop
                                                offset="0%"
                                                stopColor={
                                                    productColor || graphColor
                                                }
                                                stopOpacity={0.35}
                                            />
                                            <stop
                                                offset="100%"
                                                stopColor={
                                                    productColor || graphColor
                                                }
                                                stopOpacity={0.02}
                                            />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid
                                        vertical={false}
                                        stroke={gridStroke}
                                    />
                                    <XAxis
                                        dataKey="date"
                                        tick={{ fontSize: 11, fill: tickFill }}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11, fill: tickFill }}
                                        tickFormatter={v => `${v}`}
                                        width={48}
                                        label={{
                                            value: unitLabel,
                                            angle: -90,
                                            position: 'insideLeft',
                                            style: {
                                                textAnchor: 'middle',
                                                fill: tickFill,
                                                fontSize: 11,
                                            },
                                        }}
                                    />
                                    <Tooltip
                                        content={
                                            <ProductTooltip
                                                unitLabel={unitLabel}
                                            />
                                        }
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="displayValue"
                                        stroke={productColor || graphColor}
                                        strokeWidth={2}
                                        fillOpacity={1}
                                        fill="url(#product-spent-gradient)"
                                        dot={false}
                                        activeDot={{
                                            r: 4,
                                            fill: productColor || graphColor,
                                        }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </ChartCard>
            </ChatCardContainer>
        </div>
    );
}

CleaningReportProductSpentCard.propTypes = {
    list: PropTypes.arrayOf(PropTypes.shape({})),
    consumption: PropTypes.shape({
        source: PropTypes.string,
        water_ml: PropTypes.number,
        energy_kwh: PropTypes.number,
        by_robot: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    robotDilutions: PropTypes.arrayOf(
        PropTypes.shape({
            robot_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            sn: PropTypes.string,
            dilution_ml_per_l: PropTypes.number,
        })
    ),
    periodDates: PropTypes.shape({
        startDate: PropTypes.string,
        endDate: PropTypes.string,
    }),
};

CleaningReportProductSpentCard.defaultProps = {
    list: [],
    consumption: null,
    robotDilutions: [],
    periodDates: null,
};

export default injectIntl(CleaningReportProductSpentCard);
